<template>
  <div class="recommy-tab">
    <div class="row">
      <div class="col">
        <div class="mb-2">
          <label for="recommy-template-select" class="form-label">{{ t('SELECT_TEMPLATE') }}</label>
          <Select
            label-id="recommy-template-select"
            v-model="selectedTemplateUuid"
            :options="templateSelectOptions"
            option-label="label"
            option-value="value"
            :placeholder="t('SELECT_TEMPLATE')"
            :disabled="isArchived || isLoading"
            class="w-100"
          />
        </div>
      </div>
    </div>

    <div v-if="currentAssignment?.templateNotFoundInRecommy" class="row mt-3">
      <div class="col">
        <div class="alert alert-warning mb-0">
          {{ t('TEMPLATE_NOT_FOUND_IN_RECOMMY') }}
        </div>
      </div>
    </div>

    <div v-if="selectedTemplate && !selectedTemplate.templateNotFoundInRecommy" class="row mt-3">
      <div class="col">
        <Card>
          <template #title>{{ selectedTemplate.title }}</template>
          <template #content>
            <p class="mb-2"><strong>{{ t('LANGUAGES') }}:</strong> {{ selectedTemplate.languages?.join(', ') }}</p>
            <div v-if="(selectedTemplate.questions?.length ?? 0) > 0">
              <strong>{{ t('QUESTIONS') }}:</strong>
              <DataTable
                :value="selectedTemplate.questions"
                :pt="{ table: { class: 'mt-2' } }"
                :rowHover="true"
                size="small"
                striped-rows
              >
                <Column field="sortOrder" header="#"/>
                <Column field="title" :header="t('QUESTION')"/>
                <Column field="questionType" :header="t('TYPE')">
                  <template #body="{ data }">{{ formatQuestionType(data.questionType) }}</template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col">
        <Button
          v-if="hasChanges"
          :label="t('SAVE')"
          :disabled="isArchived || isLoading || !selectedTemplateUuid"
          @click="saveTemplate"
          class="me-2"
        />
        <Button
          v-if="isCurrentTemplateSelected"
          :label="t('REMOVE')"
          severity="danger"
          :disabled="isArchived || isLoading"
          @click="removeTemplate"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {computed, onBeforeMount, ref} from "vue";
import {injectAxios} from "@/util/inject";
import type {RecommyTemplateResponse} from "@/model/recommy-template";
import {useToast} from "@/util/toastUtils";

const props = defineProps<{
  trainingId: string;
  isArchived: boolean;
}>();

const { t } = useI18n();
const axios = injectAxios();
const toast = useToast();

const isLoading = ref(false);
const templates = ref<RecommyTemplateResponse[]>([]);
const selectedTemplateUuid = ref<string | null>(null);

interface TemplateOption {
  templateGuid: string;
  title: string;
  isCurrent: boolean;
}

onBeforeMount(async () => {
  await loadData();
});

const questionTypeTranslations: Record<string, string> = {
  numeric: "QUESTION_TYPE_NUMERIC",
  text: "QUESTION_TYPE_TEXT",
};

const formatQuestionType = (value: string): string => {
  const translationKey = questionTypeTranslations[value?.toLowerCase()];
  return translationKey ? t(translationKey) : value;
};

const templateOptions = computed<TemplateOption[]>(() => {
  return templates.value
    .filter((template) => !template.templateNotFoundInRecommy)
    .map((template) => {
      const isCurrent = template.trainings?.includes(props.trainingId);
      return {
        templateGuid: template.templateUuid,
        title: template.title ?? '',
        isCurrent: isCurrent ?? false,
      };
    });
});

const templateSelectOptions = computed(() => {
  return templateOptions.value.map((opt) => ({
    value: opt.templateGuid,
    label: (opt.isCurrent ? '✓ ' : '') + opt.title,
  }));
});

const selectedTemplate = computed<RecommyTemplateResponse | null>(() => {
  if (!selectedTemplateUuid.value) {
    return null;
  }
  return templates.value.find(
    (t) => t.templateUuid === selectedTemplateUuid.value
  ) || null;
});

const currentAssignment = computed(() => {
  return templates.value.find(
    (t) => t.trainings?.includes(props.trainingId) ?? false
  ) || null;
});

const isCurrentTemplateSelected = computed(() => {
  return !!(currentAssignment.value && selectedTemplateUuid.value === currentAssignment.value.templateUuid);
});

const hasChanges = computed(() => {
  const savedUuid = currentAssignment.value?.templateUuid || null;
  return selectedTemplateUuid.value !== savedUuid;
});

async function loadData() {
  isLoading.value = true;
  try {
    await fetchTemplates();
  } finally {
    isLoading.value = false;
  }
}

async function fetchTemplates() {
  const response = await axios.get("/recommy-templates");
  templates.value = response.data || [];
  
  const current = templates.value.find(
      (t) => t.trainings?.includes(props.trainingId) ?? false
  );
  selectedTemplateUuid.value = current?.templateNotFoundInRecommy ? null : (current?.templateUuid || null);
}

async function saveTemplate() {
  if (!selectedTemplateUuid.value) {
    return;
  }

  isLoading.value = true;
  try {
    await axios.put(`/recommy-templates/${props.trainingId}/${selectedTemplateUuid.value}`);
    toast.makeSuccessToast(t("TEMPLATE_ASSIGNED"));
    
    if (currentAssignment.value) {
      currentAssignment.value.trainings = currentAssignment.value.trainings?.filter((trainingId) => trainingId !== props.trainingId);
    }

    const newTemplate = selectedTemplate.value;
    if (newTemplate) {
      newTemplate.trainings = [...(newTemplate.trainings || []), props.trainingId];
    }
  } finally {
    isLoading.value = false;
  }
}

async function removeTemplate() {
  isLoading.value = true;
  try {
    await axios.delete(`/recommy-templates/${props.trainingId}`);
    toast.makeSuccessToast(t("TEMPLATE_REMOVED"));
    selectedTemplateUuid.value = null;
    
    if (currentAssignment.value) {
      currentAssignment.value.trainings = currentAssignment.value.trainings?.filter((trainingId) => trainingId !== props.trainingId);
    }
  } finally {
    isLoading.value = false;
  }
}
</script>
